import React, { useState, useEffect, useRef } from 'react'
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage'
import { SecondaryBase } from '../Commons/SecondaryBase'
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
import * as XLSX from 'xlsx'

// Importamos los servicios del BL
import { 
    ShowInteractiveMessage, 
    HideInteractiveMessage, 
    GetInteractiveChatConfig,
    SetInteractiveChatConfig, 
    GetQuickReplies, 
    SaveQuickReply, 
    DeleteQuickReply,
    ReorderQuickReplies
} from './../../../utilities/BL/InteractiveChat'

/**
 * ==============================================================================
 * COMPONENTE: INTERACTIVE CHAT DASHBOARD (ADMIN)
 * Panel de control optimizado para celulares para enviar mensajes a la pantalla.
 * ==============================================================================
 */
const InteractiveChat = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // --------------------------------------------------------------------------------
    // 1. ESTADOS DE DATOS Y MENSAJE
    // --------------------------------------------------------------------------------
    const [currentMessage, setCurrentMessage] = useState("");
    const [isMessageVisible, setIsMessageVisible] = useState(false);
    const [quickReplies, setQuickReplies] = useState([]);
    const [isLoadingReplies, setIsLoadingReplies] = useState(false);

    // Modal de Apariencia
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [lastSavedTime, setLastSavedTime] = useState(null);

    // --------------------------------------------------------------------------------
    // 2. ESTADOS DE CONFIGURACIÓN VISUAL
    // --------------------------------------------------------------------------------
    const [config, setConfig] = useState({
        textColor: '#FFFFFF',
        strokeColor: '#000000',
        hasBackground: true,
        backgroundColor: '#000000', // Hex para fácil uso con input color
        scale: 1.0,
        positionY: 100,
        rotation: 0,
        fontFamily: 'Montserrat'
    });

    const isLoadedRef = useRef(false);

    // --------------------------------------------------------------------------------
    // 3. AUXILIARES: FORMATO DE FECHA MÉXICO
    // --------------------------------------------------------------------------------
    const formatMexicoDate = (dateOrStr) => {
        if (!dateOrStr) return "";
        const date = new Date(dateOrStr);
        if (isNaN(date.getTime())) return "";

        const options = {
            timeZone: 'America/Mexico_City',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        };

        const formatter = new Intl.DateTimeFormat('es-MX', options);
        const parts = formatter.formatToParts(date);

        let day = "", month = "", year = "", hour = "", minute = "", dayPeriod = "";
        for (const part of parts) {
            if (part.type === 'day') day = part.value;
            else if (part.type === 'month') month = part.value;
            else if (part.type === 'year') year = part.value;
            else if (part.type === 'hour') hour = part.value;
            else if (part.type === 'minute') minute = part.value;
            else if (part.type === 'dayPeriod') {
                dayPeriod = part.value.toLowerCase().replace(/\s/g, '');
            }
        }

        let ampm = "";
        if (dayPeriod.includes('pm') || dayPeriod.includes('p.m')) {
            ampm = "p.m.";
        } else if (dayPeriod.includes('am') || dayPeriod.includes('a.m')) {
            ampm = "a.m.";
        } else {
            ampm = dayPeriod;
        }

        return `${day}/${month}/${year} ${hour}:${minute} ${ampm}`;
    };

    // --------------------------------------------------------------------------------
    // 4. CICLO DE VIDA (Carga inicial y Sincronización de Configuración)
    // --------------------------------------------------------------------------------
    
    // Cargar Respuestas Rápidas y Configuración al montar
    useEffect(() => {
        const loadRepliesAndConfig = async () => {
            setIsLoadingReplies(true);
            
            // Cargar respuestas
            const resReplies = await GetQuickReplies();
            if (resReplies.success) {
                setQuickReplies(resReplies.data || []);
            }

            // Cargar configuración
            const resConfig = await GetInteractiveChatConfig();
            if (resConfig.success && resConfig.data) {
                const rgbaToHex = (rgbaStr) => {
                    if (!rgbaStr || !rgbaStr.startsWith('rgba')) return '#000000';
                    const match = rgbaStr.match(/\d+/g);
                    if (!match || match.length < 3) return '#000000';
                    const r = parseInt(match[0]);
                    const g = parseInt(match[1]);
                    const b = parseInt(match[2]);
                    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
                };

                const loaded = { ...resConfig.data };
                if (loaded.backgroundColor && loaded.backgroundColor.startsWith('rgba')) {
                    loaded.backgroundColor = rgbaToHex(loaded.backgroundColor);
                }
                
                setConfig(loaded);
                if (loaded.updatedAt) {
                    setLastSavedTime(new Date(loaded.updatedAt));
                }
            }
            
            // Permitir auto-guardado después de que todo se haya cargado
            setTimeout(() => {
                isLoadedRef.current = true;
            }, 300);

            setIsLoadingReplies(false);
        };
        loadRepliesAndConfig();
    }, []);

    // Sincronizar los cambios de apariencia en vivo con Debounce y guardado en la BD
    useEffect(() => {
        if (!isLoadedRef.current) {
            return;
        }

        const debounceTimer = setTimeout(async () => {
            const hexToRgb = (hex) => {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `${r}, ${g}, ${b}`;
            };

            const payloadConfig = {
                ...config,
                backgroundColor: `rgba(${hexToRgb(config.backgroundColor)}, 0.7)`
            };

            const res = await SetInteractiveChatConfig(payloadConfig);
            if (res.success) {
                setLastSavedTime(new Date());
            }
        }, 500); // 500ms de debounce

        return () => clearTimeout(debounceTimer);
    }, [config]);

    // --------------------------------------------------------------------------------
    // 5. HANDLERS DE MENSAJES (PROYECTAR / OCULTAR)
    // --------------------------------------------------------------------------------
    const handleShowMessage = async () => {
        if (!currentMessage.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: "Escribe un mensaje primero." });
            return;
        }

        try {
            const res = await ShowInteractiveMessage(currentMessage);
            if (res.success) {
                setIsMessageVisible(true);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mensaje proyectado en pantalla." });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al comunicar con la pantalla." });
        }
    };

    const handleHideMessage = async () => {
        try {
            const res = await HideInteractiveMessage();
            if (res.success) {
                setIsMessageVisible(false);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mensaje ocultado." });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al quitar el mensaje." });
        }
    };

    // --------------------------------------------------------------------------------
    // 6. HANDLERS DE RESPUESTAS RÁPIDAS
    // --------------------------------------------------------------------------------
    const handleSaveQuickReply = async () => {
        if (!currentMessage.trim()) return;
        
        try {
            const res = await SaveQuickReply(currentMessage);
            if (res.success) {
                // Al insertar se coloca al principio en la BD, por lo que lo agregamos al principio del array local
                setQuickReplies(prev => [res.data, ...prev]);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Guardado en respuestas rápidas." });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "No se pudo guardar." });
        }
    };

    const handleDeleteQuickReply = async (id) => {
        try {
            const res = await DeleteQuickReply(id);
            if (res.success) {
                setQuickReplies(prev => prev.filter(r => r.id !== id));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Respuesta eliminada." });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "No se pudo eliminar." });
        }
    };

    const handleSelectQuickReply = (text) => {
        setCurrentMessage(text);
        showMessage({ type: MESSAGE_TYPE.INFO, text: "Mensaje cargado. Da clic en Proyectar para enviarlo." });
    };

    const handleMoveReply = async (index, direction) => {
        const newReplies = [...quickReplies];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (targetIndex < 0 || targetIndex >= newReplies.length) return;

        // Intercambiar posiciones en local
        const temp = newReplies[index];
        newReplies[index] = newReplies[targetIndex];
        newReplies[targetIndex] = temp;

        setQuickReplies(newReplies);

        // Persistir el orden en la base de datos
        const orderedIds = newReplies.map(r => r.id);
        const res = await ReorderQuickReplies(orderedIds);
        if (!res.success) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al guardar el nuevo orden." });
        }
    };

    // --------------------------------------------------------------------------------
    // 7. EXPORTAR A EXCEL
    // --------------------------------------------------------------------------------
    const handleExportExcel = () => {
        if (quickReplies.length === 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: "No hay respuestas rápidas para exportar." });
            return;
        }

        try {
            const dataToExport = quickReplies.map((reply, idx) => ({
                "N°": idx + 1,
                "Respuesta Rápida": reply.text,
                "Fecha de Creación": formatMexicoDate(reply.createdAtUtc)
            }));

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            
            // Ajustar anchos
            worksheet['!cols'] = [
                { wch: 6 },
                { wch: 45 },
                { wch: 25 }
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Respuestas Rápidas");

            XLSX.writeFile(workbook, "Respuestas_Rapidas_InteractiveChat.xlsx");
            
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Excel exportado correctamente." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al exportar a Excel." });
            console.error(error);
        }
    };

    // --------------------------------------------------------------------------------
    // 8. HANDLERS DE CONFIGURACIÓN VISUAL
    // --------------------------------------------------------------------------------
    const handleConfigChange = (key, value) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const ReturnToDashboard = async () => {
        try {
            await HideInteractiveMessage(); // Limpiamos la pantalla por si acaso
            await BLResetCurrentDynamic();
        } finally {
            getOutOfTheDynamic();
        }
    };

    // --------------------------------------------------------------------------------
    // 9. RENDERIZADO
    // --------------------------------------------------------------------------------
    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <div className="container-fluid py-4" style={{ maxWidth: '1100px' }}>
                
                {/* CSS Inyectado para efectos hover y modal */}
                <style>
                    {`
                    .hover-bg-black:hover {
                        background-color: rgba(255, 255, 255, 0.05) !important;
                    }
                    .hover-highlight:hover {
                        background-color: rgba(13, 202, 240, 0.1) !important;
                        cursor: pointer;
                    }
                    .small-message-text {
                        word-break: break-word;
                    }
                    `}
                </style>

                {/* HEADER */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-chat-square-quote-fill fs-2 me-3 text-white"></i>
                        <h2 className="mb-0 text-white fw-bold">Interacción en Vivo</h2>
                    </div>
                    <button 
                        className="btn btn-outline-info fw-bold py-2 px-3 rounded-3 shadow-sm d-flex align-items-center gap-2"
                        onClick={() => setIsConfigModalOpen(true)}
                    >
                        <i className="bi bi-palette-fill"></i>
                        <span>Apariencia</span>
                    </button>
                </div>

                <div className="row g-4">
                    
                    {/* PANEL IZQUIERDO: CONTROL DE MENSAJES */}
                    <div className="col-12 col-md-6">
                        <div className="card shadow border-danger bg-dark text-white h-100">
                            <div className="card-header bg-danger text-white fw-bold d-flex justify-content-between align-items-center">
                                <span><i className="bi bi-keyboard me-2"></i> Escribir Mensaje</span>
                                {isMessageVisible && <span className="badge bg-success">En Pantalla</span>}
                            </div>
                            <div className="card-body d-flex flex-column justify-content-center">
                                
                                <textarea 
                                    className="form-control mb-3 fs-5" 
                                    rows="4" 
                                    placeholder="Ej: ¿A quién le vas a dedicar el gol?"
                                    value={currentMessage}
                                    onChange={(e) => setCurrentMessage(e.target.value)}
                                    style={{ resize: 'none', backgroundColor: '#1a1a1a', color: 'white', border: '1px solid #444' }}
                                />

                                {/* BOTONES PRINCIPALES */}
                                <div className="d-flex flex-column gap-2">
                                    <button 
                                        className={`btn btn-lg fw-bold ${isMessageVisible ? 'btn-outline-danger' : 'btn-danger'}`} 
                                        onClick={handleShowMessage}
                                    >
                                        <i className="bi bi-tv me-2"></i> 
                                        {isMessageVisible ? 'Actualizar Texto en Pantalla' : 'Proyectar Mensaje'}
                                    </button>
                                    
                                    <button 
                                        className="btn btn-outline-light fw-bold" 
                                        onClick={handleHideMessage}
                                        disabled={!isMessageVisible}
                                    >
                                        <i className="bi bi-eye-slash me-2"></i> Quitar de la Pantalla
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PANEL DERECHO: LISTADO VERTICAL DE RESPUESTAS RÁPIDAS */}
                    <div className="col-12 col-md-6">
                        <div className="card shadow border-info bg-dark text-white h-100">
                            <div className="card-header bg-info text-white fw-bold d-flex justify-content-between align-items-center">
                                <span><i className="bi bi-lightning-charge me-2"></i> Respuestas Rápidas</span>
                                <div className="d-flex gap-2">
                                    <button 
                                        className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                                        onClick={handleExportExcel}
                                        title="Exportar listado a Excel"
                                    >
                                        <i className="bi bi-file-earmark-excel"></i>
                                        <span>Excel</span>
                                    </button>
                                    <button 
                                        className="btn btn-sm btn-info text-white fw-bold" 
                                        onClick={handleSaveQuickReply} 
                                        disabled={!currentMessage.trim()}
                                    >
                                        Guardar Actual
                                    </button>
                                </div>
                            </div>
                            
                            <div className="card-body d-flex flex-column" style={{ maxHeight: '450px' }}>
                                <div className="overflow-auto pe-1 flex-grow-1" style={{ maxHeight: '380px' }}>
                                    {isLoadingReplies ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border spinner-border-sm text-info" role="status"></div>
                                            <span className="text-muted ms-2 small">Cargando respuestas...</span>
                                        </div>
                                    ) : quickReplies.length === 0 ? (
                                        <div className="text-center py-5 text-muted small fst-italic">
                                            <i className="bi bi-chat-dots fs-3 d-block mb-2 text-secondary"></i>
                                            No has guardado respuestas rápidas.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-2">
                                            {quickReplies.map((reply, index) => (
                                                <div 
                                                    key={reply.id} 
                                                    className="d-flex align-items-center justify-content-between p-2 rounded border border-secondary border-opacity-20 bg-black bg-opacity-20 hover-bg-black"
                                                    style={{ transition: 'background-color 0.2s' }}
                                                >
                                                    {/* Flechas de ordenamiento */}
                                                    <div className="d-flex flex-column gap-1 me-2">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-xs btn-outline-secondary p-0 px-1 border-0 text-white" 
                                                            onClick={() => handleMoveReply(index, 'up')}
                                                            disabled={index === 0}
                                                            style={{ fontSize: '0.75rem', lineHeight: '1' }}
                                                        >
                                                            <i className="bi bi-chevron-up"></i>
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-xs btn-outline-secondary p-0 px-1 border-0 text-white" 
                                                            onClick={() => handleMoveReply(index, 'down')}
                                                            disabled={index === quickReplies.length - 1}
                                                            style={{ fontSize: '0.75rem', lineHeight: '1' }}
                                                        >
                                                            <i className="bi bi-chevron-down"></i>
                                                        </button>
                                                    </div>

                                                    {/* Texto de la respuesta rápida y fecha */}
                                                    <div 
                                                        className="flex-grow-1 text-start px-2 py-1 cursor-pointer rounded hover-highlight"
                                                        onClick={() => handleSelectQuickReply(reply.text)}
                                                        style={{ minWidth: 0 }}
                                                    >
                                                        <div className="text-white text-wrap fw-semibold small-message-text" style={{ fontSize: '0.9rem' }}>
                                                            {reply.text}
                                                        </div>
                                                        <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                            <i className="bi bi-clock me-1"></i>
                                                            Creado: {formatMexicoDate(reply.createdAtUtc)}
                                                        </div>
                                                    </div>

                                                    {/* Acción de borrar */}
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger border-0 rounded-circle ms-2"
                                                        onClick={() => handleDeleteQuickReply(reply.id)} 
                                                        title="Eliminar respuesta"
                                                        style={{ width: '32px', height: '32px', padding: '0' }}
                                                    >
                                                        <i className="bi bi-trash-fill"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* MODAL DE CONFIGURACIÓN VISUAL (APARIENCIA) */}
            {isConfigModalOpen && (
                <>
                    <div className="cModal__Background" onClick={() => setIsConfigModalOpen(false)}></div>
                    <div className="cModal__Container" style={{ maxWidth: '500px' }}>
                        <div className="cModal__Container__Content bg-dark text-white border border-info rounded-3 p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="modal-title fw-bold text-info"><i className="bi bi-palette me-2"></i> Apariencia Visual</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setIsConfigModalOpen(false)}></button>
                            </div>
                            
                            <div className="modal-body py-2">
                                {/* FUENTE */}
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted">Tipo de Letra:</label>
                                    <select 
                                        className="form-select form-select-sm bg-dark text-white border-secondary"
                                        value={config.fontFamily}
                                        onChange={(e) => handleConfigChange('fontFamily', e.target.value)}
                                    >
                                        <option value="Montserrat">Montserrat (Predeterminada)</option>
                                        <option value="Bebas Neue">Bebas Neue (Impacto)</option>
                                        <option value="Roboto">Roboto (Limpia)</option>
                                        <option value="Caveat">Caveat (Decorativa)</option>
                                    </select>
                                </div>

                                {/* COLORES */}
                                <div className="row g-3 mb-3">
                                    <div className="col-4 text-center">
                                        <label className="d-block small fw-bold text-muted mb-1">Color Texto</label>
                                        <input type="color" className="form-control form-control-color w-100 bg-dark border-secondary p-1" value={config.textColor} onChange={(e) => handleConfigChange('textColor', e.target.value)} />
                                    </div>
                                    <div className="col-4 text-center">
                                        <label className="d-block small fw-bold text-muted mb-1">Color Borde</label>
                                        <input type="color" className="form-control form-control-color w-100 bg-dark border-secondary p-1" value={config.strokeColor} onChange={(e) => handleConfigChange('strokeColor', e.target.value)} />
                                    </div>
                                    <div className="col-4 text-center">
                                        <label className="d-block small fw-bold text-muted mb-1">Color Fondo</label>
                                        <input type="color" className="form-control form-control-color w-100 bg-dark border-secondary p-1" value={config.backgroundColor} onChange={(e) => handleConfigChange('backgroundColor', e.target.value)} disabled={!config.hasBackground} />
                                    </div>
                                </div>

                                {/* TOGGLE FONDO */}
                                <div className="form-check form-switch mb-3 pb-2 border-bottom border-secondary">
                                    <input className="form-check-input" type="checkbox" checked={config.hasBackground} onChange={(e) => handleConfigChange('hasBackground', e.target.checked)} />
                                    <label className="form-check-label small fw-bold">Mostrar plasta semitransparente de fondo</label>
                                </div>

                                {/* SLIDERS */}
                                <div className="mb-3">
                                    <label className="d-flex justify-content-between small fw-bold text-muted mb-1">
                                        <span>Tamaño (Escala)</span>
                                        <span className="text-info">{config.scale}x</span>
                                    </label>
                                    <input type="range" className="form-range" min="0.5" max="3" step="0.1" value={config.scale} onChange={(e) => handleConfigChange('scale', parseFloat(e.target.value))} />
                                </div>

                                <div className="mb-3">
                                    <label className="d-flex justify-content-between small fw-bold text-muted mb-1">
                                        <span>Posición Vertical (Y)</span>
                                        <span className="text-info">{config.positionY}px</span>
                                    </label>
                                    <input type="range" className="form-range" min="0" max="800" step="10" value={config.positionY} onChange={(e) => handleConfigChange('positionY', parseInt(e.target.value))} />
                                    <small className="text-muted" style={{fontSize: '0.65rem'}}>Mueve el texto desde abajo hacia arriba</small>
                                </div>

                                <div className="mb-3">
                                    <label className="d-flex justify-content-between small fw-bold text-muted mb-1">
                                        <span>Inclinación (Rotación)</span>
                                        <span className="text-info">{config.rotation}°</span>
                                    </label>
                                    <input type="range" className="form-range" min="-45" max="45" step="1" value={config.rotation} onChange={(e) => handleConfigChange('rotation', parseInt(e.target.value))} />
                                </div>
                            </div>

                            {/* FOOTER DEL MODAL */}
                            <div className="d-flex flex-column align-items-center mt-3 pt-3 border-top border-secondary">
                                {lastSavedTime && (
                                    <div className="text-success small mb-2 d-flex align-items-center">
                                        <i className="bi bi-cloud-check-fill me-1"></i>
                                        <span>Guardado: {formatMexicoDate(lastSavedTime)}</span>
                                    </div>
                                )}
                                <button className="btn btn-primary w-100 fw-bold" onClick={() => setIsConfigModalOpen(false)}>
                                    Listo / Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </SecondaryBase>
    );
};

export { InteractiveChat };